import cn from "classnames";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Dropdown } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import Segments from "components/navigation/segments/Segments";
import { DetailsHeader } from "../../../components/sectionHeader";
import ReportingExport from "components/reportingExport/ReportingExport";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { selectIsExportActive } from "../state/export/exportSlice";
import { selectIsReportEnabled } from "../state/toolbar/toolbarSlice";
import { useAccountFilterShouldShow } from "./queries/useAccountFilterShouldShow";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { usePerformanceBreadcrumbs } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import { ContentReportsView } from "./ContentReportsView";

import styles from "./contentOverviewReports.module.scss";
import { useHasPermissions } from "features/Application/hooks";
import { RolePermissions, RouteNames } from "enums";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";

type OwnProps = {
  usesDropdownMenuNavigation?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

export type Props = PropsFromRedux & OwnProps;

export const ContentOverviewReports: FC<Props> = ({
  accountId,
  accountType,
  isExportEnabled,
  selectIsReportEnabled,
  usesDropdownMenuNavigation,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLinksAssetTypeEnabled = useFeatureFlag(FeatureFlags.LinkAssetType);
  const isMessagesReportEnabled = useFeatureFlag(FeatureFlags.MessagesReport);
  const hasManagePermission = useHasPermissions([RolePermissions.CampaignsManage]);
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);
  const isVideosTab = location.pathname.endsWith("videos");
  const isFlowsTab = location.pathname.endsWith("flows");

  const initialOverrides = useMemo(() => {
    if (usesDropdownMenuNavigation && childAccountId) {
      return { accounts: [childAccountId] as number[], includeMyData: false };
    }
    return undefined;
  }, [usesDropdownMenuNavigation, childAccountId]);

  const [filter, setFilter, resetFilter] = usePerformanceFilter({
    accountFilterShouldShow: false,
    includeAccountsDropdown: accountFilterShouldShow,
    includeDistinct: true,
    supportsSPType: true,
    accountId,
    includeVideoType: isVideosTab,
    includePriority: isFlowsTab,
    includeStatus: isFlowsTab,
    initialOverrides,
  });

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay, resetBreadcrumbs, goBackNLevels } =
    usePerformanceBreadcrumbs();

  useEffect(() => {
    resetBreadcrumbs();
    resetFilter();
  }, [location.pathname, resetBreadcrumbs, resetFilter]);

  const ignoreInCountAndHide = useMemo(() => {
    const ignore = ["type"];
    if (hasBreadcrumbsToDisplay) {
      ignore.push("showCustomers");
    }
    if (usesDropdownMenuNavigation) {
      ignore.push("accounts", "includeMyData");
    }
    return ignore;
  }, [hasBreadcrumbsToDisplay, usesDropdownMenuNavigation]);

  const toolbar = useMemo(
    () =>
      selectIsReportEnabled ? (
        <div className={styles.right}>
          <ReportingFilter
            currentFilter={filter}
            callback={setFilter}
            includeAccounts={false}
            includeAccountsDropdown={accountFilterShouldShow}
            ignoreInCountAndHide={ignoreInCountAndHide}
            includeDistinct
            supportsSPType
            includeVideoType={isVideosTab}
            includePriority={isFlowsTab}
            includeStatus={isFlowsTab}
            initialOverrides={initialOverrides}
          />
          {isExportEnabled && <ReportingExport />}
        </div>
      ) : (
        <div />
      ),
    [
      filter,
      setFilter,
      accountFilterShouldShow,
      isExportEnabled,
      selectIsReportEnabled,
      isVideosTab,
      isFlowsTab,
      ignoreInCountAndHide,
      initialOverrides,
    ],
  );

  const pages = useMemo(() => {
    const pages = [
      { to: "flows", label: "Flows" },
      { to: "videos", label: "Videos" },
      { to: "surveys", label: "Surveys" },
      { to: "assessments", label: "Assessments" },
      { to: "pdfs", label: "PDFs" },
      { to: "emails", label: "Emails" },
      { to: "events", label: "Events" },
    ];

    if (isMessagesReportEnabled) {
      pages.push({ to: "messages", label: "Messages" });
    }
    if (hasManagePermission) {
      pages.push({ to: "campaigns", label: "Campaigns" });
    }
    if (isLinksAssetTypeEnabled) {
      pages.push({ to: "links", label: "Links" });
    }
    return pages;
  }, [hasManagePermission, isLinksAssetTypeEnabled, isMessagesReportEnabled]);

  const renderDropdownMenuNavigation = useCallback(() => {
    const currentPage = pages.find((p) => location.pathname.includes(`/${p.to}`)) ?? pages[0];

    return (
      <Dropdown
        className={styles.overviewDropdown}
        text={currentPage.label}
        icon={<FontAwesomeIcon className={styles.icon} icon={faChevronDown} />}
        floating
      >
        <Dropdown.Menu>
          {pages.map((page) => (
            <Dropdown.Item
              key={page.to}
              active={currentPage.to === page.to}
              onClick={() => {
                navigate(`../${page.to}`, { relative: "path" });
                resetBreadcrumbs();
              }}
              text={page.label}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [pages, location.pathname, navigate, resetBreadcrumbs]);

  const renderTabNavigation = (
    <>
      <DetailsHeader title="Content" showReportLastUpdated />
      <Segments to={`/${RouteNames.reporting}/content`}>
        {pages.map((page, i) => (
          <Segments.Segment key={i} {...page} />
        ))}
      </Segments>
    </>
  );

  const renderHeader = useCallback(() => {
    if (hasBreadcrumbsToDisplay) {
      return <div>{domElements}</div>;
    } else if (!selectIsReportEnabled) {
      return null;
    } else if (usesDropdownMenuNavigation) {
      return renderDropdownMenuNavigation();
    }
    return <h2>Summary</h2>;
  }, [
    hasBreadcrumbsToDisplay,
    domElements,
    selectIsReportEnabled,
    usesDropdownMenuNavigation,
    renderDropdownMenuNavigation,
  ]);

  return (
    <div
      className={cn({
        "nested-content": !usesDropdownMenuNavigation,
        [styles.rootDropdownMenuNavigation]: usesDropdownMenuNavigation,
      })}
    >
      {!usesDropdownMenuNavigation && renderTabNavigation}
      <div
        className={cn(styles.body, {
          [styles.reportUnavailable]: !selectIsReportEnabled,
          [styles.drilldownReportUnavailable]: !selectIsReportEnabled,
        })}
      >
        <div className={styles.header}>
          {renderHeader()}
          {selectIsReportEnabled && toolbar}
        </div>
        <ContentReportsView
          filter={filter}
          selectIsReportEnabled={selectIsReportEnabled}
          usesDropdownMenuNavigation={usesDropdownMenuNavigation}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
          registerBreadcrumb={registerBreadcrumb}
          resetBreadcrumbs={resetBreadcrumbs}
          goBackNLevels={goBackNLevels}
          accountFilterShouldShow={accountFilterShouldShow ?? false}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountType: state.userProfile.accountTypeId,
  isExportEnabled: selectIsExportActive(state),
  selectIsReportEnabled: selectIsReportEnabled(state),
});

const mapDispatchToProps = (_dispatch: AppDispatch) => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ContentOverviewReports);
export default ConnectedComponent;
